import { CaseData, CMSSection, Openhouse } from "@/cms/models";
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faQuestion } from "@fortawesome/sharp-light-svg-icons";
import * as ics from "ics";
import { isToday, isTomorrow } from "date-fns";
import {
  Lato,
  Merriweather,
  Montserrat,
  Roboto,
  Roboto_Slab,
  Titillium_Web,
  Cormorant_Garamond,
  Plus_Jakarta_Sans,
  Quicksand,
  Arya,
  Prata,
} from "next/font/google";

const cormorantGaramond = Cormorant_Garamond({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const plusJakartaSans = Plus_Jakarta_Sans({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const classicFont = Roboto_Slab({
  weight: ["300", "400", "600", "700"],
  subsets: ["latin"],
  display: "swap",
});

const modernFont = Roboto({
  weight: ["300", "700"],
  subsets: ["latin"],
  display: "swap",
});

const latoFont = Lato({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const titilliumWeb = Titillium_Web({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const montserrat = Montserrat({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const merriweather = Merriweather({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const standardFont = Roboto({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const quicksand = Quicksand({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const aryaFont = Arya({
  weight: ["400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const prataFont = Prata({
  weight: ["400"],
  subsets: ["latin"],
  display: "swap",
});

function getOpenHouseTextDays(openHouse: Openhouse): string {
  if (isToday(openHouse.startDate!)) {
    return "I dag";
  } else if (isTomorrow(openHouse.startDate!)) {
    return "I morgen";
  }
  return formatDate(openHouse!.startDate!, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

export function transformImageUrl(url?: string | undefined | null) {
  if (url && url?.length > 0) {
    const mediaServer = process.env.MEDIA_SERVER_URL ?? "";
    if (mediaServer.length > 0) {
      const replacedUrl = url.replace(mediaServer, "/media/");
      return replacedUrl;
    }
    return url;
  }
  return url;
}

export function GetFont(fontType: string) {
  switch (fontType) {
    case "classic":
      return classicFont;
    case "modern":
      return modernFont;
    case "lato":
      return latoFont;
    case "titillium_web":
      return titilliumWeb;
    case "montserrat":
      return montserrat;
    case "merriweather":
      return merriweather;
    case "cormorantGaramond":
      return cormorantGaramond;
    case "plusJakartaSans":
      return plusJakartaSans;
    case "roboto-montserrat":
      return montserrat;
    case "montserrat-only":
      return montserrat;
    case "quicksand":
      return quicksand;
    case "arya":
      return aryaFont;
    case "prata":
      return prataFont;
    default:
      return standardFont;
  }
}

export function getOpenHouseText(openHouse: Openhouse): string {
  return [
    capitalize(getOpenHouseTextDays(openHouse)),
    formatTime(openHouse.startDate!),
    "-",
    formatTime(openHouse!.endDate!),
  ].join(" ");
}

export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
) {
  options = options ?? {};
  if (date instanceof Date) {
    return date.toLocaleDateString("da-DK", options);
  }
  return new Date(date).toLocaleDateString("da-DK", options);
}

export function fullScreen(ref: React.RefObject<any>, isFullscreen: boolean) {
  if (!isFullscreen) {
    if (ref.current) {
      if (ref.current.requestFullscreen) {
        ref.current.requestFullscreen();
      } else if (ref.current.webkitRequestFullscreen) {
        ref.current.webkitRequestFullscreen();
      } else if (ref.current.mozRequestFullScreen) {
        ref.current.mozRequestFullScreen();
      } else if (ref.current.msRequestFullscreen) {
        ref.current.msRequestFullscreen();
      }
    }
  } else {
    document.exitFullscreen();
  }
}
export function formatTime(date: string | Date) {
  if (date instanceof Date) {
    return date.toLocaleTimeString("da-DK", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return new Date(date).toLocaleTimeString("da-DK", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function createIcsEvent(
  title: string,
  startDate: Date,
  endDate: Date,
  location?: string
): string {
  return ics.createEvent({
    title: title,
    location: location,
    start: [
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startDate.getHours(),
      startDate.getMinutes(),
    ],
    end: [
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endDate.getHours(),
      endDate.getMinutes(),
    ],
  })!.value!;
}

export function mapSocialIcon(namespace: string) {
  switch (namespace) {
    case "some.facebook":
      return faFacebookF;
    case "some.linkedin":
      return faLinkedinIn;
    case "some.instagram":
      return faInstagram;
    case "some.x":
      return faXTwitter;
    case "some.email":
      return faEnvelope;
    default:
      return faQuestion;
  }
}

export function formatToIcs(date: string | Date | undefined) {
  if (!date) return "";
  const dateObj = date instanceof Date ? date : new Date(date);
  return dateObj.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

export function capitalize(s: string) {
  return s && String(s[0]).toUpperCase() + String(s).slice(1);
}

export function formatNumber(
  number: number | string,
  options?: { defaultValue?: string; fragments?: number }
) {
  options = options ?? {};
  const defaultValue = options.defaultValue ?? "";
  const fragments = options.fragments ?? 0;

  if (number === undefined || number === null || number === "")
    return defaultValue;

  if (typeof number === "string") number = parseFloat(number);

  return new Intl.NumberFormat("da-DK", {
    minimumFractionDigits: fragments,
    maximumFractionDigits: fragments,
  }).format(number);
}

export function formatMonthlyRental(number?: number | string) {
  if (typeof number === "string") number = parseFloat(number);

  const rentAmount = new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    notation: "standard",
    maximumFractionDigits: 0,
  }).format(number ?? 0);

  return `${rentAmount} / md.`;
}

export function formatYearlyRental(number?: number | string) {
  if (typeof number === "string") number = parseFloat(number);

  const rentAmount = new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    notation: "standard",
    maximumFractionDigits: 0,
  }).format(number ?? 0);

  return `${rentAmount} / år`;
}

export function formatPrice(number?: number | string) {
  if (typeof number === "string") number = parseFloat(number);

  return new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    notation: "standard",
    maximumFractionDigits: 0,
  }).format(number ?? 0);
}

export function isHouse(list: string[]) {
  const houseClassifications = [
    "dk.realequity.estate.villa",
    "dk.realequity.estate.raekkehus",
    "dk.realequity.estate.fritidshus",
  ];
  return list.some((x) => houseClassifications.includes(x));
}

export function isApartment(list: string[]) {
  const apartmentClassifications = [
    "dk.realequity.taxonomy.ejerform.ejerlejlighed",
  ];
  return list.some((x) => apartmentClassifications.includes(x));
}

export function isAgricultural(list: string[]): boolean {
  const agriculturalClassifications = [
    "dk.realequity.taxonomy.kategori.landbrug",
  ];
  return list.some((x) => agriculturalClassifications.includes(x));
}

export function isCountryEstate(list: string[]): boolean {
  const countryEstateClassifications = [
    "dk.realequity.taxonomy.markeringerpaaejendom.landejendom",
  ];
  return list.some((x) => countryEstateClassifications.includes(x));
}

export function hasAgriculturalTypes(list: string[]) {
  const agriculturalTypes = [
    "dk.realequity.estate.planteavl",
    "dk.realequity.estate.svineproduktion",
    "dk.realequity.estate.skovlandbrugsejendom",
    "dk.realequity.estate.kvaegbrug",
  ];
  return list.some((x) => agriculturalTypes.includes(x));
}

export function isFarm(list: string[]) {
  const farmClassifications = [
    "dk.realequity.estate.landbrugsejendom",
    "dk.realequity.estate.lystejendom",
  ];
  return list.some((x) => farmClassifications.includes(x));
}

export function isCommercial(list: string[]) {
  const commercialClassifications = [
    "dk.realequity.taxonomy.kategori.erhverv",
    "dk.realequity.estate.butikdetail",
  ];
  return list.some((x) => commercialClassifications.includes(x));
}

export function isHomeSale(list: string[]) {
  const homeSaleClassifications = ["dk.realequity.taxonomy.opdrag.salg"];
  return list.some((x) => homeSaleClassifications.includes(x));
}

export function isInvestmentProperty(list: string[]) {
  const investmentPropertyClassifications = [
    "dk.realequity.taxonomy.markeringerpaaejendom.investeringsejendom",
  ];
  return list.some((x) => investmentPropertyClassifications.includes(x));
}

export function isOwnProperty(list: string[]) {
  const ownPropertyClassifications = [
    "dk.realequity.taxonomy.markeringerpaaejendom.brugerejendom",
  ];
  return list.some((x) => ownPropertyClassifications.includes(x));
}

export function isAndels(list: string[]) {
  const andelsClassifications = [
    "dk.realequity.taxonomy.ejerform.andel",
    "dk.realequity.taxonomy.opdrag.salg",
  ];
  return andelsClassifications.every((x) => list.includes(x));
}

export function isLand(list: string[]) {
  const landClassifications = [
    "dk.realequity.taxonomy.markeringerpaaejendom.grund",
  ];
  return list.some((x) => landClassifications.includes(x));
}

export function isRental(list: string[]) {
  const rentalClassifications = ["dk.realequity.taxonomy.opdrag.udlejning"];
  return list.some((x) => rentalClassifications.includes(x));
}

export function isLejeBolig(list: string[]) {
  const houseClassifications = [
    "dk.realequity.taxonomy.kategori.helaarsbolig",
    "dk.realequity.taxonomy.kategori.fritidsbolig",
  ];
  return isRental(list) && list.some((x) => houseClassifications.includes(x));
}

export function hasParking(list: string[]) {
  const parkingClassifications = [
    "dk.realequity.taxonomy.markeringerpaaejendom.parkering",
  ];
  return list.some((x) => parkingClassifications.includes(x));
}

export function petsAllowed(list: string[]) {
  return list.some((x) =>
    ["dk.realequity.taxonomy.markeringerpaaejendom.husdyrtilladt"].includes(x)
  );
}

export function sublettingAllowed(list: string[]) {
  return list.some((x) =>
    ["dk.realequity.taxonomy.markeringerpaaejendom.fremlejetilladt"].includes(x)
  );
}

export function hasElevator(list: string[]) {
  const parkingClassifications = [
    "dk.realequity.taxonomy.markeringerpaaejendom.elevator",
  ];
  return list.some((x) => parkingClassifications.includes(x));
}

export function getBalconyTaxon(caseData: CaseData) {
  return caseData.estateTaxons.find(
    (x) =>
      x.taxonNamespace === "dk.realequity.taxonomy.markeringerpaaejendom.altan"
  );
}

export function getCaseClassification(property: CaseData) {
  return [
    ...property.taxons,
    ...property.estateTaxons.map((x) => x.taxonNamespace),
    property.estateNamespace,
  ];
}

export function getEstateFacilities(property: CaseData) {
  return property.estateTaxons.filter((x) =>
    x.taxonNamespace.startsWith("dk.realequity.taxonomy.markeringerpaaejendom")
  );
}

export function squareMetersToHectares(squareMeters: number): number {
  const hectares = squareMeters / 10000;
  return parseFloat(hectares.toFixed(2));
}

export function getDefaultCmsSection(
  cmsSection: CMSSection<any>,
  defaultUI?: any
): CMSSection<any> {
  return {
    ...cmsSection,
    ui: {
      ...{
        width: "2xl",
        padding: "md | lg",
        marginTop: "none",
        marginBottom: "none",
      },
      ...defaultUI,
      ...cmsSection?.ui,
    },
  };
}

export function getPriceInfo(property: CaseData): string {
  const list = getCaseClassification(property);

  if (isRental(list) && property.isCommercial) {
    return property.isActive && property.rentAnnually > 0
      ? formatYearlyRental(property.rentAnnually)
      : "-";
  }

  if (isRental(list)) {
    return property.isActive && property.rentMonthly > 0
      ? formatMonthlyRental(property.rentMonthly)
      : "-";
  }

  return property.isActive && property.price > 0
    ? formatPrice(property.price)
    : "-";
}

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export function getLabel(item: CaseData) {
  if (item.isJustSold) {
    return "købsaftale underskrevet";
  }

  if (!item.isActive && !item.isSold) {
    return "ikke på markedet";
  }

  if (isRental(getCaseClassification(item)) && item.isSold) {
    return "udlejet";
  }

  if (item.isSold) {
    return "solgt";
  }

  if (item.openhouses.find((x) => x.bookingRequired)) {
    return "åbent hus med tilmelding";
  }

  if (item.openhouses?.length > 0) {
    return "åbent hus";
  }

  if (item.marketingTexts?.label) {
    return item.marketingTexts?.label;
  }

  if (item.isCommercial || item.isAgricultural) {
    return item.estateType;
  }
}

export function getCaseDetailSublabel(item: CaseData) {
  if (item.isJustSold) {
    return "købsaftale underskrevet";
  }

  if (!item.isActive && !item.isSold) {
    return "ikke på markedet";
  }

  if (isRental(getCaseClassification(item)) && item.isSold) {
    return "udlejet";
  }

  if (item.isSold) {
    return "solgt";
  }
}

export function getCaseUrl(property: {
  advertisementAddressOrDisplayName: string;
  zipAndCity: string;
  id: string;
}) {
  return `/ejendom/${encodeURIComponent(property.advertisementAddressOrDisplayName.replace(" ", "-").toLowerCase())}-${encodeURIComponent(property.zipAndCity.replace(" ", "-").toLowerCase())}____${property.id}`;
}

export function formatPhoneNumberObject(data: {
  prefix?: string;
  number?: string;
}) {
  return formatPhoneNumber(data?.prefix ?? "", data?.number ?? "");
}

export function formatPhoneNumber(
  prefix: string,
  phone: string | undefined
): string {
  if (!phone) return "";

  const digits = phone.replace(/\s+/g, "");

  if (prefix === "+45") {
    const digitGroups = digits.match(/.{1,2}/g) || [];
    const formattedDigits = digitGroups.join(" ");
    return prefix ? `${prefix} ${formattedDigits}` : formattedDigits;
  }

  return prefix ? `${prefix} ${digits}` : digits;
}

export function getPrimaryOrFirst<T extends { primary: boolean }>(
  list: T[]
): T | undefined {
  return list.find((item) => item.primary) || list[0];
}

export const DefaultTake = 99;
